import React, { useState, useEffect } from "react";
import { injectIntl, IntlShape } from "react-intl";
import { toast } from "react-toastify";
import { Location } from "history";
import { match as Match } from "react-router-dom";
import { GroundGraphqlContextStore } from "../../../../../lib/ground-aws-graphql-core";
import { EnumPaths } from "../../../../../utils/navigation";
import history from "../../../../../history";
import Table from "../../../../../components/Table";
import Dropdown, {
  DropdownValues,
} from "../../../../../components/Tailwind/Dropdown";
import images from "../../../../../images";
import { getTranslation } from "../../../../../utils/translation";
import ConfirmModal from "../../../../../utils/modal/confirm";
import IntlMessages from "../../../../../utils/messages";
import { displayDayDDMMYYYY_HHMM } from "../../../../../utils/config";
import Button from "../../../../../components/Tailwind/Button";
import { TableChangeParams } from "../../../../../components/Table/types";
import { Checkpoint } from "../../../../../lib/ground-aws-graphql-core/models/Api/Inspection";
import Badge from "../../../../../components/Tailwind/Badge";

interface Props {
  match: Match<{ cid: string }>;
  intl: IntlShape;
  typeId: string;
  location?: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const DEFAULT_LIMIT = 5;

const ListCheckpoints = (props: Props): JSX.Element => {
  const { intl, typeId, location, match } = props;
  const [loading, setLoading] = useState(false);

  const [showDeleteCheckpointModal, setShowDeleteCheckpointModal] =
    useState(false);

  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state || null
  );

  const [checkpointId, setCheckpointId] = useState<number>();

  const listCheckpoints = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.listCheckpoints
  );

  const checkpoints = GroundGraphqlContextStore.useStoreState(
    actions => actions.inspection.checkpoints.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    actions => actions.inspection.checkpoints.total
  );

  const setCheckpoint = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.setCheckpoint
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const deleteCheckpoint = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.deleteCheckpoint
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = (pageIndex = 0, loader = true, limit = DEFAULT_LIMIT) => {
    if (typeId) {
      if (loader) setLoading(true);
      listCheckpoints({
        checkpointTypeId: typeId,
        offset: pageIndex * limit,
        limit,
      }).finally(() => setLoading(false));
    }
  };

  const addNewCheckpoint = () => {
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${typeId}/checkpoints/${EnumPaths.CREATION_MODE}`
    );
  };

  const handleOnChange = (changeParams: TableChangeParams) => {
    const { pageIndex, limit } = changeParams;
    setInitialParams(changeParams);
    fetchData(pageIndex, false, limit);
  };

  const handleEditCheckpoint = (id: number) => {
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${typeId}/checkpoints/${id}/${EnumPaths.EDIT_MODE}`;

    setCheckpoint(null);
    history.push(editLink);
  };

  const handleDelete = () => {
    setLoading(true);
    setShowDeleteCheckpointModal(false);
    deleteCheckpoint({ checkpointTypeId: typeId, checkpointId: checkpointId! })
      .then(() => {
        toast(
          intl.formatMessage({
            id: "page.inspection.checkpoint.delete.success",
          }),
          { type: "success" }
        );
      })
      .catch(() => {
        toast(
          intl.formatMessage({
            id: "page.inspection.checkpoint.delete.error",
          }),
          {
            type: "error",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const tableHead = [
    "page.inspection.checkpoint.label",
    "page.inspection.checkpoint.created_at",
    "page.inspection.checkpoint.updated_at",
    "general.status",
    "general.actions",
  ];

  const tableBody = checkpoints.map((checkpoint: Checkpoint) => {
    const { id, title, created_at, updated_at, enabled } = checkpoint;
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${typeId}/checkpoints/${id}/${EnumPaths.EDIT_MODE}`;

    const dropdownValues: DropdownValues[] = [
      {
        id: "edit_inspection_checkpoint_key",
        label: "page.list.inspection.checkpoints.edit",
        icon: images.edit,
        link: editLink,
      },
      {
        id: "delete",
        label: "general.delete",
        icon: images.deleteIcon,
        onClick: () => {
          setShowDeleteCheckpointModal(!showDeleteCheckpointModal);
          setCheckpointId(id);
        },
      },
    ];

    const elements = [
      {
        element: getTranslation(title),
        onCellClick: () => handleEditCheckpoint(id),
      },
      {
        element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
        onCellClick: () => handleEditCheckpoint(id),
      },
      {
        element: displayDayDDMMYYYY_HHMM(updated_at, centerTimezone),
        onCellClick: () => handleEditCheckpoint(id),
      },
      {
        element: enabled ? (
          <Badge label="general.actif" bgClassName="bg-ground-green-100" />
        ) : (
          <Badge label="general.inactif" bgClassName="bg-red-500" />
        ),
        onCellClick: () => handleEditCheckpoint(id),
      },
      {
        element: (
          <Dropdown
            values={dropdownValues}
            dataCy={`inspection-checkpoint-dropdown-${id}`}
          />
        ),
      },
    ];

    return {
      id: checkpoint.id.toString(),
      rowElements: elements,
    };
  });

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between bg-white px-8 h-20 w-full">
        <div className="flex items-center">
          <h3 className="text-16px leading-6 font-medium text-gray-900">
            <IntlMessages id="page.list.inspection.checkpoints.title" />
          </h3>
          <span className="text-17px text-ground-blue-100 leading-5 ml-1">
            ({total})
          </span>
        </div>
        <Button
          id="btn-create-inspection-checkpoint"
          name="btn-create-inspection-checkpoint"
          item={null}
          outline
          type="button"
          onClick={addNewCheckpoint}
        >
          <IntlMessages id="page.list.inspection.checkpoint.create.checkpoint" />
        </Button>
      </div>

      <Table
        head={tableHead}
        body={tableBody}
        noDataText="page.list.inspection.checkpoints.empty"
        onChange={handleOnChange}
        paginationLimit={DEFAULT_LIMIT}
        paginationTotal={total}
        loading={loading}
        setLoading={setLoading}
      />

      <ConfirmModal
        isOpen={showDeleteCheckpointModal}
        onRequestClose={() =>
          setShowDeleteCheckpointModal(!showDeleteCheckpointModal)
        }
        toggle={() => setShowDeleteCheckpointModal(!showDeleteCheckpointModal)}
        handleConfirm={handleDelete}
        content={<IntlMessages id="page.list.inspection.checkpoints.delete" />}
      />
    </div>
  );
};

export default injectIntl(ListCheckpoints);
